import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, App, Button, Progress } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import ChangeCardSourceOffer from '../collections/ChangeCardSourceOffer';
import { undoCountsLine } from '../collections/ChangeCardTaskRow';
import { confirmDestructive } from '../common/confirmDestructive';
import { getChangeCardTask, getChangeCardUndo, undoChangeCardTask } from '../../services/changeCard';
import { revertChange } from '../../services/history';
import { apiErrorMessage } from '../../utils/apiErrors';
import {
	GO_TO_ITEMS,
	SEE_IN_IMPORTS,
	UNDO,
	UNDO_CARD_CHANGE,
	UNDO_CONFIRM_TITLE,
	backgroundMovingLine,
	backgroundProgressLine,
	couldNotMoveLead,
	itemsText,
	leftBehindResultLines,
	moreLine,
	movedLine,
	ruleResultLine,
	skippedRows,
	skippedTotal,
	undoConfirmBody,
	undoneLine,
	warningText,
	wereSkippedLead,
} from './reviewCopy';

export const TASK_POLL_MS = 3000;

const importsPath = collectionId => `/collections/${collectionId}/form?tab=imports`;
const itemsPath = collectionId => `/collections/${collectionId}/items`;

function Failures({ failures = [], total = 0 }) {
	if (!failures.length && !total) return null;
	return (
		<ul className="change-card__review-list">
			{failures.map(f => <li key={`${f.id}:${f.code}`}>{f.message || f.code}</li>)}
			{total > failures.length ? <li className="change-card__muted">{moreLine(total - failures.length)}</li> : null}
		</ul>
	);
}

function Counts({ failed = 0, failures = [], skipped = {}, ctx }) {
	const skippedCount = skippedTotal(skipped);
	return (
		<>
			{failed ? (
				<>
					<p className="change-card__note">{couldNotMoveLead(failed)}</p>
					<Failures failures={failures} total={failed} />
				</>
			) : null}
			{skippedCount ? (
				<>
					<p className="change-card__note">{wereSkippedLead(skippedCount)}</p>
					<ul className="change-card__review-list">
						{skippedRows(skipped, ctx).map(row => <li key={row.reason}>{itemsText(row.count)} — {row.words}</li>)}
					</ul>
				</>
			) : null}
		</>
	);
}

/**
 * A change made in the request (200): what moved, what did not, where the
 * data left behind went, the rules it ran — and Undo, which takes every
 * moved item back through its own History row (KPR-704), one at a time.
 */
function RequestResult({ result, ctx, collectionId }) {
	const { modal } = App.useApp();
	const navigate = useNavigate();
	const [undo, setUndo] = useState(null);
	const [undoing, setUndoing] = useState(false);
	const moved = useMemo(() => result.moved || [], [result.moved]);
	const items = result.items || {};

	const runUndo = useCallback(async () => {
		setUndoing(true);
		let reverted = 0;
		const failures = [];
		for (const row of moved) {
			try {
				// eslint-disable-next-line no-await-in-loop -- one item at a time, as the task's undo does
				await revertChange(row.historyId);
				reverted += 1;
			} catch (err) {
				failures.push({ id: row.id, code: err?.response?.data?.code || 'failed', message: apiErrorMessage(err, '') });
			}
		}
		setUndo({ reverted, failures });
		setUndoing(false);
	}, [moved]);

	const askUndo = () => confirmDestructive(modal, {
		title: UNDO_CONFIRM_TITLE,
		content: undoConfirmBody(moved.length, ctx),
		okText: UNDO_CARD_CHANGE,
		onOk: runUndo,
	});

	return (
		<section className="change-card__panel change-card__result" aria-label="The change">
			{undo ? (
				<>
					<p className="change-card__lead" role="status">{undoneLine(undo.reverted, undo.failures.length, ctx)}</p>
					<Failures failures={undo.failures} />
				</>
			) : (
				<>
					<p className="change-card__lead" role="status">{movedLine(items.moved ?? moved.length, ctx)}</p>
					<Counts failed={items.failed} failures={result.failures} skipped={items.skipped} ctx={ctx} />
					{leftBehindResultLines(result.leftBehind).map(line => <p key={line} className="change-card__note">{line}</p>)}
					{(result.rules || []).length ? (
						<ul className="change-card__review-list">
							{result.rules.map((rule, i) => <li key={rule.id || `rule-${i}`}>{ruleResultLine(rule)}</li>)}
						</ul>
					) : null}
					{(result.warnings || []).map(w => {
						const shown = warningText(w, ctx);
						return shown.text ? <Alert key={w.code} type="warning" showIcon message={shown.text} className="change-card__warning" /> : null;
					})}
				</>
			)}
			<div className="change-card__result-actions">
				<Button type="primary" onClick={() => navigate(itemsPath(collectionId))}>{GO_TO_ITEMS}</Button>
				{!undo && moved.length ? <Button onClick={askUndo} loading={undoing}>{UNDO}</Button> : null}
			</div>
		</section>
	);
}

/**
 * A change running as a background task (202): its progress while it runs,
 * read every few seconds, then what became of the items and Undo card
 * change once the task says it can be undone (KPR-705, KPR-704). The same
 * task is a row of Settings → Imports.
 */
function TaskResult({ result, ctx, collectionId }) {
	const { modal, message } = App.useApp();
	const navigate = useNavigate();
	const taskId = result.task?.id;
	const [report, setReport] = useState({ task: result.task, items: null, failures: [] });
	const [undoState, setUndoState] = useState(null);
	const [error, setError] = useState(null);
	const [starting, setStarting] = useState(false);
	const timer = useRef(null);

	const load = useCallback(async () => {
		if (!taskId) return;
		try {
			const data = await getChangeCardTask(collectionId, taskId);
			setReport({ task: data.task, items: data.items, failures: data.failures || [] });
			setError(null);
			if (!data.task?.running) {
				const undo = await getChangeCardUndo(collectionId, taskId);
				setUndoState(undo);
			}
		} catch (err) {
			setError(err);
		}
	}, [collectionId, taskId]);

	const running = !!report.task?.running || !!undoState?.undo?.running;
	useEffect(() => {
		load();
	}, [load]);
	useEffect(() => {
		if (!running) return undefined;
		timer.current = setInterval(load, TASK_POLL_MS);
		return () => clearInterval(timer.current);
	}, [running, load]);

	const task = report.task || {};
	const progress = task.progress || {};
	const total = progress.total || result.items?.requested || 0;
	const percent = total ? Math.min(100, Math.round(((progress.done || 0) / total) * 100)) : 0;
	const undo = undoState?.undo || null;

	const askUndo = () => confirmDestructive(modal, {
		title: UNDO_CONFIRM_TITLE,
		content: undoConfirmBody(progress.moved || 0, ctx),
		okText: UNDO_CARD_CHANGE,
		onOk: async () => {
			setStarting(true);
			try {
				const started = await undoChangeCardTask(collectionId, taskId);
				setUndoState(prev => ({ ...(prev || {}), undo: started.undo, undoable: false }));
			} catch (err) {
				message.error(apiErrorMessage(err, 'The card change could not be undone. Nothing was changed.'));
			} finally {
				setStarting(false);
			}
		},
	});

	const hasData = !!report.task;
	return (
		<section className="change-card__panel change-card__result" aria-label="The change">
			<DataState
				state={deriveDataState({ loading: !hasData && !error, error, hasData })}
				hasData={hasData}
				error={error}
				onRetry={load}
				skeleton={<Skeleton variant="line" count={2} />}
				label="the card change"
			>
				{undo ? (
					<>
						<p className="change-card__lead" role="status">{undo.running ? undoCountsLine(undo.progress || {}) : undoneLine(undo.progress?.reverted || 0, undo.progress?.failed || 0, ctx)}</p>
						<Failures failures={undo.running ? [] : (undoState.failures || [])} total={undo.running ? 0 : (undo.progress?.failed || 0)} />
					</>
				) : task.running ? (
					<>
						<p className="change-card__lead">{backgroundMovingLine(total, ctx)}</p>
						<p className="change-card__note" role="status">{backgroundProgressLine({ done: progress.done, total })}</p>
						<Progress percent={percent} size="small" showInfo={false} aria-label={`${percent}% worked through`} />
					</>
				) : (
					<>
						<p className="change-card__lead" role="status">{movedLine(progress.moved || 0, ctx)}</p>
						{task.status === 'failed' && task.error ? <Alert type="error" showIcon message={task.error} /> : null}
						<Counts failed={report.items?.failed || progress.failed} failures={report.failures} skipped={report.items?.skipped || progress.skippedBy} ctx={ctx} />
						{(progress.moved || 0) > 0 ? <ChangeCardSourceOffer collectionId={collectionId} card={task.card} /> : null}
					</>
				)}
			</DataState>
			<div className="change-card__result-actions">
				<Button type="primary" onClick={() => navigate(itemsPath(collectionId))}>{GO_TO_ITEMS}</Button>
				<Link to={importsPath(collectionId)}>{SEE_IN_IMPORTS}</Link>
				{!task.running && !undo && undoState?.undoable ? (
					<Button onClick={askUndo} loading={starting}>{UNDO_CARD_CHANGE}</Button>
				) : null}
			</div>
		</section>
	);
}

/**
 * CHANGE CARD — after the change (KPR-709): the move's result, or its
 * background task, each with Undo while it is allowed.
 */
export default function ChangeCardResult({ result, ctx, collectionId }) {
	if (!result) return null;
	return result.applied
		? <RequestResult result={result} ctx={ctx} collectionId={collectionId} />
		: <TaskResult result={result} ctx={ctx} collectionId={collectionId} />;
}
